import React, { useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { useFormik, FormikHelpers } from 'formik'
import { Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { ArrowRightIcon, TrashIcon, XMarkIcon } from '@heroicons/react/16/solid'

import { useAddHotel } from 'api/hotels/addHotel'
import { useGetHotelResource } from 'api/hotels/getHotelResource'
import { buildUpdateHotelQueryKey, UpdateGdsHotelRequestBodyPart, useUpdateHotel } from 'api/hotels/updateHotel'
import { useGetGdsListResource } from 'api/gds/getGdsListResource'
import { notification } from 'helpers'
import { parseGdsListToObject } from 'helpers/gds'
import { getErrorMessage } from 'helpers/error'
import useSubmitOnEnter from 'hooks/useSubmitOnEnter'
import { SABRE_ID, TRAVEL_PORT_ID } from 'constants/gds'
import { Button, Header, Input } from 'ui-v2'

import validationSchema from './validationSchema'

const INITIAL_GDS_FIELDS_FORM = {
  id: undefined,
  gds_id: undefined,
  chain_code: '',
  property_id: '',
}

type GdsFieldsForm = {
  id: Nullable<number>
  gds_id?: number
  chain_code: string
  property_id: string
}

type FormValues = {
  name: string
  country: string
  city: string
  address: string
  travel_port: GdsFieldsForm
  sabre: GdsFieldsForm
  email: string
}

type Props = {
  isEdit?: boolean
}

const trimValue = (value: string) => value.trim()

const trimGdsFields = (gdsFields: GdsFieldsForm): GdsFieldsForm => ({
  ...gdsFields,
  chain_code: trimValue(gdsFields.chain_code),
  property_id: trimValue(gdsFields.property_id),
})

const trimFormValues = (values: FormValues): FormValues => ({
  ...values,
  name: trimValue(values.name),
  country: trimValue(values.country),
  city: trimValue(values.city),
  address: trimValue(values.address),
  email: trimValue(values.email),
  travel_port: trimGdsFields(values.travel_port),
  sabre: trimGdsFields(values.sabre),
})

const getInitialCreateFormValues = (): FormValues => ({
  name: '',
  country: '',
  city: '',
  address: '',
  email: '',
  travel_port: {
    ...INITIAL_GDS_FIELDS_FORM,
    gds_id: TRAVEL_PORT_ID,
  },
  sabre: {
    ...INITIAL_GDS_FIELDS_FORM,
    gds_id: SABRE_ID,
  },
})

const PropertyForm: React.FC<Props> = ({ isEdit = false }) => {
  const { hotelId } = useParams<{ hotelId: string }>()
  const { push: routerPush } = useHistory()
  const parsedHotelId = parseInt(hotelId || '0', 10)

  const queryClientCache = useQueryClient()
  const gdsListResource = useGetGdsListResource()
  const hotelResource = useGetHotelResource({
    params: { id: parsedHotelId },
    queryOptions: {
      enabled: isEdit && !!parsedHotelId,
    },
  })
  const addHotel = useAddHotel({})
  const updateHotel = useUpdateHotel({ params: { id: parsedHotelId } })

  const getInitialGdsFields = useCallback(
    (gdsId?: number): GdsFieldsForm => ({
      ...INITIAL_GDS_FIELDS_FORM,
      gds_id: gdsId,
    }),
    [],
  )

  const getInitialFormValues = useCallback((): FormValues => {
    if (!isEdit) {
      return getInitialCreateFormValues()
    }

    const hotelData = hotelResource.data?.data
    const gdsData = gdsListResource.data?.data
    const gdsList = gdsData ? parseGdsListToObject(gdsData) : null

    if (isEdit && gdsList && hotelData) {
      const travelPortData = hotelData.gds_hotels.find((item) => item.gds_id === gdsList.travel_port.id)
      const sabreData = hotelData.gds_hotels.find((item) => item.gds_id === gdsList.sabre.id)

      return {
        name: hotelData.name,
        country: hotelData.country || '',
        city: hotelData.city || '',
        address: hotelData.address || '',
        travel_port: travelPortData || getInitialGdsFields(gdsList.travel_port.id),
        sabre: sabreData || getInitialGdsFields(gdsList.sabre.id),
        email: hotelData.email || '',
      }
    }

    return getInitialCreateFormValues()
  }, [gdsListResource.data?.data, hotelResource.data?.data, getInitialGdsFields, isEdit])

  const backToList = useCallback(() => {
    routerPush(`/admin/properties`)
  }, [routerPush])

  const onSubmit = useCallback(
    async (data: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
      formikHelpers.setSubmitting(true)
      const trimmedData = trimFormValues(data)
      const gdsHotels = [trimmedData.travel_port, trimmedData.sabre].filter(
        (gdsData: GdsFieldsForm): gdsData is UpdateGdsHotelRequestBodyPart =>
          !!(gdsData.gds_id && gdsData.property_id && gdsData.chain_code),
      )

      try {
        if (isEdit) {
          const hotelData = hotelResource.data?.data ? hotelResource.data.data : {}
          const newHotelData = await updateHotel.mutateAsync({
            ...hotelData,
            name: trimmedData.name,
            email: trimmedData.email,
            gds_hotels: gdsHotels,
          })

          queryClientCache.setQueryData(buildUpdateHotelQueryKey({ params: { id: parsedHotelId } }), () => newHotelData)

          notification.success({ message: 'Success.' })
          backToList()
          return
        }

        const newHotelData = await addHotel.mutateAsync({
          name: trimmedData.name,
          country: trimmedData.country,
          city: trimmedData.city,
          address: trimmedData.address,
          gds_hotels: gdsHotels,
        })

        notification.success({ message: 'Property added successfully' })
        routerPush(`/admin/properties/${newHotelData.data.id}`)
      } catch (error) {
        notification.error({ message: getErrorMessage(error) })
        formikHelpers.setSubmitting(false)
      }
    },
    [addHotel, backToList, hotelResource.data?.data, isEdit, parsedHotelId, queryClientCache, routerPush, updateHotel],
  )

  const { values, errors, setFieldValue, isSubmitting, handleSubmit } = useFormik<FormValues>({
    initialValues: getInitialFormValues(),
    enableReinitialize: isEdit,
    onSubmit,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: validationSchema(isEdit),
  })

  useSubmitOnEnter(handleSubmit)

  if (isEdit && hotelResource.isLoading) {
    return (
      <div className="flex justify-center items-center w-full mt-[75px]">
        <Spin spinning indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
      </div>
    )
  }

  return (
    <form>
      <div className="flex flex-col justify-between gap-4 bg-bg-light">
        <Header
          size="big"
          title={isEdit ? hotelResource.data?.data.name || '' : 'New property'}
          className="flex justify-between border-b border-indigo-100"
        >
          <Button
            variant="secondary"
            className="p-1 w-[34px] rounded flex items-center justify-center"
            onClick={backToList}
          >
            <XMarkIcon className="w-5" />
          </Button>
        </Header>
        <div className="grid grid-cols-3 gap-y-6 gap-x-4 p-2 lg:p-4 mx-[30px] bg-white rounded-lg">
          <div className="col-span-2 p-2 bg-bg-light rounded-md md:p-6 relative flex flex-col gap-2 lg:gap-4">
            <h2 className="text-lg leading-8 tracking-wider uppercase text-gray-800 font-semibold">General</h2>
            <div className="flex w-full gap-2 flex-row">
              <Input
                label="Name"
                value={values.name}
                onChange={(value) => setFieldValue('name', value)}
                error={errors?.name}
                className="flex-1"
              />
              {isEdit ? (
                <Input label="Internal ID" value={hotelResource.data?.data.id.toString() || ''} readOnly isDisabled />
              ) : (
                <Input
                  label="Country"
                  value={values.country}
                  onChange={(value) => setFieldValue('country', value)}
                  error={errors?.country}
                  className="flex-1"
                />
              )}
            </div>
            {!isEdit && (
              <div className="flex w-full gap-2 flex-row">
                <Input
                  label="City"
                  value={values.city}
                  onChange={(value) => setFieldValue('city', value)}
                  error={errors?.city}
                  className="flex-1"
                />
                <Input
                  label="Address"
                  value={values.address}
                  onChange={(value) => setFieldValue('address', value)}
                  error={errors?.address}
                  className="flex-1"
                />
              </div>
            )}
          </div>
          {isEdit && (
            <div className="p-2 bg-bg-light rounded-md md:p-6 relative flex flex-col gap-2 lg:gap-4">
              <h2 className="text-lg leading-8 tracking-wider uppercase text-gray-800 font-semibold">Hotel email</h2>
              <Input
                label="Email"
                value={values.email}
                error={errors?.email}
                onChange={(value) => setFieldValue('email', value)}
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-y-6 gap-x-4 p-2 lg:p-4 mx-[30px] bg-white rounded-lg">
          <div className="p-2 bg-bg-light rounded-md md:p-6 relative flex flex-col gap-2 lg:gap-4">
            <h2 className="text-lg leading-8 tracking-wider uppercase text-gray-800 font-semibold">Sabre</h2>
            <div className="flex gap-2 flex-row">
              <Input
                value={values.sabre.property_id}
                error={errors?.sabre?.property_id}
                onChange={(value) => setFieldValue('sabre.property_id', value)}
                label="Property ID"
                className="flex-1"
              />
              <Input
                label="Chain code"
                value={values.sabre.chain_code}
                error={errors?.sabre?.chain_code}
                onChange={(value) => setFieldValue('sabre.chain_code', value)}
                className="flex-1"
              />
            </div>
          </div>
          <div className="p-2 bg-bg-light rounded-md md:p-6 relative flex flex-col gap-2 lg:gap-4">
            <h2 className="text-lg leading-8 tracking-wider uppercase text-gray-800 font-semibold">Travelport</h2>
            <div className="flex gap-2 flex-row">
              <Input
                value={values.travel_port.property_id}
                error={errors?.travel_port?.property_id}
                onChange={(value) => setFieldValue('travel_port.property_id', value)}
                label="Property ID"
                className="flex-1"
              />
              <Input
                label="Chain code"
                value={values.travel_port.chain_code}
                error={errors?.travel_port?.chain_code}
                onChange={(value) => setFieldValue('travel_port.chain_code', value)}
                className="flex-1"
              />
            </div>
          </div>
          <div className="col-span-2 ml-auto mt-2 flex items-center justify-end gap-2">
            <Button size="large" onClick={backToList}>
              <span className="flex items-center gap-2">
                Cancel <TrashIcon className="w-5" />
              </span>
            </Button>

            <Button size="large" variant="primary" onClick={() => handleSubmit()} isLoading={isSubmitting}>
              <span className="flex items-center gap-2">
                Save <ArrowRightIcon className="w-5" />
              </span>
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}

export const PropertyCreateForm: React.FC = () => <PropertyForm />

export const PropertyEditForm: React.FC = () => <PropertyForm isEdit />

export default PropertyForm
